/**
 * Model class for Exemption entry validation
 */

import Project from '@/models/Project'
import * as _ from 'lodash'
import { requiredIf } from 'vuelidate/lib/validators'

const ownerStatusOptions = [
    { value: 'DRAFT', text: 'Draft', badgeVariant: 'warning' },
    { value: 'PENDING', text: 'Submitted', badgeVariant: 'info' },
]

const adminStatusOptions = [
    { value: 'GRANTED', text: 'Granted', badgeVariant: 'success' },
    { value: 'DENIED', text: 'Denied', badgeVariant: 'danger' },
]

const allStatusOptions = _.concat(ownerStatusOptions, adminStatusOptions)

export default {
    allStatusOptions: allStatusOptions,
    getStatusLabel (value) {
        const option = _.find(allStatusOptions, (opt) => {
            return opt.value === value
        })
        return (option ? option.text : '')
    },
    getStatusBadgeVariant (value) {
        const option = _.find(this.allStatusOptions, (opt) => {
            return opt.value === value
        })
        return (option ? option.badgeVariant : '')
    },
    getStatusBadgeVariantByLabel (text) {
        const option = _.find(this.allStatusOptions, (opt) => {
            return opt.text === text
        })
        return (option ? option.badgeVariant : '')
    },

    fields: {
        project_id: {
            label: 'Project',
            type: 'select',
            options: [],
            async loadOptions (model) {
                this.options = _.map(model.equipment.companyProjects, (cp) => {
                    return {
                        value: cp.project_id, text: cp.projectName
                    }
                })
            },
            showIf (model) {
                return model.type === 'EMERGENCY' ||
                    model.type === 'NO_RENTAL' ||
                    model.type === 'COBID'
            },
            validations: {
                required: requiredIf(function () {
                    return this.fieldModel.fields.project_id.showIf(this.form) // this = DataForm
                })
            },
        },
        'project.name': { // read-only
            label: 'Project',
            type: 'string'
        },
        type: {
            label: 'Reason for Exemption',
            type: 'radios',
            options: [
                { value: 'EMERGENCY', text: 'Emergency' },
                { value: 'NO_RENTAL', text: 'No appropriate rental equipment available' },
                { value: 'COBID', text: 'COBID certified firm' },
                { value: 'UNIQUE', text: 'Unique circumstances' },
                { value: 'SAFETY', text: 'Safety impacts' },
                { value: 'RENEWABLE', text: 'Renewable Diesel' },
            ],
            getDescription (model) {
                if (model.type === 'EMERGENCY') {
                    return `Emergency: The equipment/vehicle is required for an emergency (including for underground equipment 
                    operators). Provide description of the situation and include the project and agency.`
                } else if (model.type === 'NO_RENTAL') {
                    return `No Rental Available: No appropriate rental equipment/vehicle is available. Provide company name and contact information for rental companies contacted.`
                } else if (model.type === 'COBID') {
                    return `COBID Certified Firm: Working on a Port of Portland or Metro project.
                        Provide name of project and agency along with your COBID certification number.`
                } else if (model.type === 'UNIQUE') {
                    return `Unique Circumstances: After following the Compliance Options Protocol, the contractor can demonstrate that 
                    due to the uniqueness of the equipment/vehicle or similar special circumstances, it is not
                    reasonable to comply with the diesel engine requirement for a specific piece of equipment/vehicle.
                    Provide a detailed description of the unique circumstances. Depending on the circumstances, 
                    additional documentation or clarification may be requested.`
                } else if (model.type === 'SAFETY') {
                    return `Safety Impacts: After following the Compliance Options Protocol, the required emission control device 
                    would obscure operator lines of sight or otherwise impact worker safety or the equipment is not 
                    able to be retrofit with a verified emission control device; and no compliant rental equipment 
                    is available within 100 miles of the job site.
                    Provide documentation from a manufacturer approved retrofit installer describing the safety 
                    impacts and provide company name and contact information for rental companies contacted.`
                } else if (model.type === 'RENEWABLE') {
                    return `Renewable Diesel: The equipment cannot be retrofitted, but the contractor commits to 
                    only using renewable diesel for the duration of the project. Provide description of your company's 
                    procedures for ensuring that this equipment is fueled with renewable diesel for the project. 
                    Include source of fuel, how you plan to fuel the equipment and your internal tracking procedures. 
                    Additional documentation including receipts may be required prior to exemption approval or 
                    throughout the project.`
                }
                return null
            }
        },
        notes: {
            label: 'Additional notes/justification',
            type: 'textarea',
        },
        ownerStatus: {  // for EO editing only
            dbFieldName: 'status',
            label: 'Status of Request',
            type: 'radios',
            options: ownerStatusOptions,
        },
        status: {  // read-only
            label: 'Status of Request',
            type: 'select',
            options: allStatusOptions,
        },
        granted_thru: {  // read-only
            label: 'Granted Through',
            type: 'string',
        },
        expiration: {
            label: 'Request Expiration',
            type: 'date'
        },
    }
}
